import React, { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import api from '../api';
import { MusicContainer, SectionHeading, EmptyText } from '../components/MusicPageStyles';
import { PageTitle, LoadingText } from '../components/Style';
import { trackEvent } from '../analytics';

var SearchWrapper = styled.div`
  width: 100%;
  margin-top: 0.5rem;
`;

var SearchFieldWrapper = styled.div`
  position: relative;
  width: 100%;
`;

var ClearButton = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #999;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem;
  line-height: 1;

  &:hover {
    color: #333;
  }
`;

var SearchSubtext = styled.div`
  font-size: 0.75rem;
  color: #999;
  margin-top: 0.35rem;
  text-align: center;
`;

var SearchInput = styled.input`
  width: 100%;
  padding: 0.6rem 0.8rem;
  font-size: 0.9rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-family: inherit;
  background: white;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #999;
  }
`;

var ResultSection = styled.div`
  width: 100%;
`;

var ResultRow = styled(Link)`
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #eee;
  text-decoration: none;
  color: inherit;
  gap: 0.6rem;

  &:hover {
    background: #f9f9f9;
  }
`;

var ResultArtwork = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
`;

var ResultText = styled.div`
  flex: 1;
  min-width: 0;
`;

var ResultTitle = styled.div`
  font-weight: 700;
  font-size: 0.85rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

var ResultMeta = styled.div`
  font-size: 0.75rem;
  color: #999;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

var TypeBadge = styled.span`
  font-size: 0.65rem;
  color: #999;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0.1rem 0.3rem;
  flex-shrink: 0;
`;

var ShowAllButton = styled.button`
  background: none;
  border: none;
  color: #999;
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0.4rem 0;
  font-family: inherit;
  width: 100%;
  text-align: center;

  &:hover {
    color: #333;
  }
`;

export default function MusicSearch() {
  var _searchParams = useSearchParams();
  var searchParams = _searchParams[0];
  var setSearchParams = _searchParams[1];

  var initialQuery = searchParams.get('q') || '';

  var _query = useState(initialQuery);
  var query = _query[0];
  var setQuery = _query[1];

  var _results = useState(null);
  var results = _results[0];
  var setResults = _results[1];

  var _loading = useState(false);
  var loading = _loading[0];
  var setLoading = _loading[1];

  var debounceRef = useRef(null);

  var _showAllArtists = useState(false);
  var showAllArtists = _showAllArtists[0];
  var setShowAllArtists = _showAllArtists[1];

  var _showAllAlbums = useState(false);
  var showAllAlbums = _showAllAlbums[0];
  var setShowAllAlbums = _showAllAlbums[1];

  var _showAllSongs = useState(false);
  var showAllSongs = _showAllSongs[0];
  var setShowAllSongs = _showAllSongs[1];

  var [artistAppleIds, setArtistAppleIds] = useState({});

  function doSearch(term) {
    setLoading(true);
    setShowAllArtists(false);
    setShowAllAlbums(false);
    setShowAllSongs(false);
    var mediaPromise = api.get('/api/search', {
      params: { term: term, limit: 25, types: 'artists,albums,songs' },
    });
    var artistPromise = api.get('/api/search/artists', { params: { q: term } }).catch(function () {
      return { data: [] };
    });
    Promise.all([mediaPromise, artistPromise])
      .then(function (responses) {
        setResults(responses[0].data);
        // Build appleId lookup from artist search results
        var idMap = {};
        (responses[1].data || []).forEach(function (a) {
          if (a.appleId) idMap[a.artist.toLowerCase()] = a.appleId;
        });
        setArtistAppleIds(idMap);
        setLoading(false);
        trackEvent('music_search_performed', {
          query: term,
          resultCount: (responses[0].data || []).length,
        });
      })
      .catch(function () {
        setResults([]);
        setLoading(false);
      });
  }

  // Restore results when navigating back with ?q= in URL
  useEffect(function () {
    if (initialQuery) {
      doSearch(initialQuery);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSearch(value) {
    setQuery(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim()) {
      setResults(null);
      setSearchParams({}, { replace: true });
      setShowAllArtists(false);
      setShowAllAlbums(false);
      setShowAllSongs(false);
      return;
    }

    debounceRef.current = setTimeout(function () {
      setSearchParams({ q: value.trim() }, { replace: true });
      doSearch(value.trim());
    }, 400);
  }

  // Group results by type and derive unique artists
  var artists = [];
  var albums = [];
  var songs = [];
  var seenArtists = {};

  if (results && Array.isArray(results)) {
    results.forEach(function (item) {
      // Collect unique artists from all results
      if (item.artist && !seenArtists[item.artist.toLowerCase()]) {
        seenArtists[item.artist.toLowerCase()] = true;
        artists.push({ artist: item.artist });
      }

      if (item.type === 'album') {
        albums.push(item);
      } else if (item.type === 'song') {
        songs.push(item);
      }
    });
  }

  var PREVIEW_LIMIT = 3;
  var displayArtists = showAllArtists ? artists : artists.slice(0, PREVIEW_LIMIT);
  var displayAlbums = showAllAlbums ? albums : albums.slice(0, PREVIEW_LIMIT);
  var displaySongs = showAllSongs ? songs : songs.slice(0, PREVIEW_LIMIT);

  var hasResults = artists.length > 0 || albums.length > 0 || songs.length > 0;

  var searchPath = '/music/search' + (query.trim() ? '?q=' + encodeURIComponent(query.trim()) : '');

  var navState = { from: 'search', fromPath: searchPath };

  function artistLink(name, appleId) {
    return (
      '/artist/' +
      encodeURIComponent(name) +
      (appleId ? '?appleId=' + encodeURIComponent(appleId) : '')
    );
  }

  function albumLink(item) {
    return (
      '/music/album?artist=' +
      encodeURIComponent(item.artist) +
      '&album=' +
      encodeURIComponent(item.album_title || '') +
      (item.upc ? '&upc=' + encodeURIComponent(item.upc) : '')
    );
  }

  function songLink(item) {
    if (item.song_title && item.artist) {
      return (
        '/music/song?artist=' +
        encodeURIComponent(item.artist) +
        '&song=' +
        encodeURIComponent(item.song_title) +
        (item.isrc ? '&isrc=' + encodeURIComponent(item.isrc) : '')
      );
    }
    if (item.id) return '/music/' + item.id;
    return '#';
  }

  return (
    <MusicContainer>
      <PageTitle>Search Music</PageTitle>

      <SearchWrapper>
        <SearchFieldWrapper>
          <SearchInput
            type="text"
            placeholder="Artists, albums, songs..."
            value={query}
            onChange={function (e) {
              handleSearch(e.target.value);
            }}
            autoFocus
          />
          {query && (
            <ClearButton
              onClick={function () {
                handleSearch('');
              }}
            >
              x
            </ClearButton>
          )}
        </SearchFieldWrapper>
        {!results && <SearchSubtext>Search the Apple Music Catalog</SearchSubtext>}
      </SearchWrapper>

      {loading && <LoadingText>Searching...</LoadingText>}

      {!loading && results && !hasResults && query.trim() && (
        <EmptyText>Nothing yet. Try a different search.</EmptyText>
      )}

      {!loading && hasResults && (
        <>
          {artists.length > 0 && (
            <ResultSection>
              <SectionHeading>Artists</SectionHeading>
              {displayArtists.map(function (item, i) {
                return (
                  <ResultRow
                    key={'artist-' + i}
                    to={artistLink(item.artist, artistAppleIds[item.artist.toLowerCase()])}
                    state={navState}
                    onClick={function () {
                      trackEvent('music_search_result_clicked', {
                        type: 'artist',
                        name: item.artist,
                      });
                    }}
                  >
                    <ResultText>
                      <ResultTitle>{item.artist}</ResultTitle>
                    </ResultText>
                  </ResultRow>
                );
              })}
              {artists.length > PREVIEW_LIMIT && !showAllArtists && (
                <ShowAllButton
                  onClick={function () {
                    setShowAllArtists(true);
                  }}
                >
                  Show all {artists.length} artists
                </ShowAllButton>
              )}
            </ResultSection>
          )}

          {albums.length > 0 && (
            <ResultSection>
              <SectionHeading>Albums</SectionHeading>
              {displayAlbums.map(function (item, i) {
                var artwork = item.artwork_url
                  ? item.artwork_url.replace('{w}', '88').replace('{h}', '88')
                  : '';
                return (
                  <ResultRow
                    key={'album-' + i}
                    to={albumLink(item)}
                    state={navState}
                    onClick={function () {
                      trackEvent('music_search_result_clicked', {
                        type: 'album',
                        name: item.album_title,
                        artist: item.artist,
                      });
                    }}
                  >
                    {artwork && <ResultArtwork src={artwork} alt={item.album_title} />}
                    <ResultText>
                      <ResultTitle>{item.album_title}</ResultTitle>
                      <ResultMeta>{item.artist}</ResultMeta>
                    </ResultText>
                    <TypeBadge>Album</TypeBadge>
                  </ResultRow>
                );
              })}
              {albums.length > PREVIEW_LIMIT && !showAllAlbums && (
                <ShowAllButton
                  onClick={function () {
                    setShowAllAlbums(true);
                  }}
                >
                  Show all {albums.length} albums
                </ShowAllButton>
              )}
            </ResultSection>
          )}

          {songs.length > 0 && (
            <ResultSection>
              <SectionHeading>Songs</SectionHeading>
              {displaySongs.map(function (item, i) {
                var artwork = item.artwork_url
                  ? item.artwork_url.replace('{w}', '88').replace('{h}', '88')
                  : '';
                return (
                  <ResultRow
                    key={'song-' + i}
                    to={songLink(item)}
                    state={navState}
                    onClick={function () {
                      trackEvent('music_search_result_clicked', {
                        type: 'song',
                        name: item.song_title,
                        artist: item.artist,
                      });
                    }}
                  >
                    {artwork && <ResultArtwork src={artwork} alt={item.song_title} />}
                    <ResultText>
                      <ResultTitle>{item.song_title}</ResultTitle>
                      <ResultMeta>
                        {item.artist}
                        {item.album_title ? ' \u00B7 ' + item.album_title : ''}
                      </ResultMeta>
                    </ResultText>
                    <TypeBadge>Song</TypeBadge>
                  </ResultRow>
                );
              })}
              {songs.length > PREVIEW_LIMIT && !showAllSongs && (
                <ShowAllButton
                  onClick={function () {
                    setShowAllSongs(true);
                  }}
                >
                  Show all {songs.length} songs
                </ShowAllButton>
              )}
            </ResultSection>
          )}
        </>
      )}
    </MusicContainer>
  );
}
